/**
 * Single source of truth for the API host the UI talks to.
 *
 * The new API lives on its own origin (api.ampersandbrands.com /
 * dev.api.ampersandbrands.com), not behind the Cockpit static origin. Every
 * request goes through `apiFetch` (which sets `credentials: 'include'`), so the
 * session cookie scoped to `.ampersandbrands.com` flows cross-origin.
 *
 * The host is resolved at RUNTIME from the browser hostname, NOT from the Vite
 * build mode. CI (`deploy-dev.yml`) builds with `pnpm build:ui` (production mode)
 * and deploys that same artifact to the dev bucket, so any build-time stage
 * signal is unreliable: it would emit the prod host on the dev site. Deriving
 * from `window.location.hostname` is correct no matter how or where the bundle
 * was built. The API host mirrors the UI host with `cockpit` swapped for `api`:
 *   dev.cockpit.ampersandbrands.com -> dev.api.ampersandbrands.com
 *   cockpit.ampersandbrands.com     -> api.ampersandbrands.com
 *
 * `VITE_API_BASE` still overrides (local dev proxy: set it to '' so paths stay
 * same-origin and the Vite proxy forwards `/api` to a local runner).
 */

const DEV_API = 'https://dev.api.ampersandbrands.com';

function resolveApiBase(): string {
  const override = import.meta.env.VITE_API_BASE;
  if (typeof override === 'string') return override;
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host.includes('cockpit')) {
      return `https://${host.replace('cockpit', 'api')}`;
    }
  }
  // localhost, IPs, preview hosts, or SSR: default to dev.
  return DEV_API;
}

export const API_BASE = resolveApiBase();
